import { cn } from '@/lib/utils';

function Skeleton({
  className,
  ...props
}: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn('animate-pulse rounded-md bg-slate-700/50', className)}
      {...props}
    />
  );
}

// Pre-built skeleton components
function CardSkeleton() {
  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 space-y-4">
      <div className="flex items-start justify-between">
        <div className="space-y-2 flex-1">
          <Skeleton className="h-5 w-3/4" />
          <div className="flex gap-2">
            <Skeleton className="h-6 w-24" />
            <Skeleton className="h-6 w-20" />
          </div>
        </div>
        <Skeleton className="h-6 w-6 rounded-full" />
      </div>
      <Skeleton className="h-4 w-full" />
      <Skeleton className="h-4 w-2/3" />
      <div className="flex justify-between">
        <Skeleton className="h-4 w-32" />
        <Skeleton className="h-4 w-24" />
      </div>
      <Skeleton className="h-9 w-full" />
    </div>
  );
}

function TimelineCardSkeleton() {
  return (
    <div className="relative flex gap-4">
      <div className="flex flex-col items-center">
        <Skeleton className="w-4 h-4 rounded-full" />
        <div className="w-0.5 bg-slate-700 flex-grow mt-2" />
      </div>
      <div className="flex-1 pb-8">
        <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-4 space-y-3">
          <Skeleton className="h-5 w-3/4" />
          <div className="flex gap-2">
            <Skeleton className="h-6 w-28" />
            <Skeleton className="h-6 w-20" />
          </div>
          <Skeleton className="h-4 w-full" />
          <div className="flex gap-4">
            <Skeleton className="h-4 w-32" />
            <Skeleton className="h-4 w-24" />
          </div>
          <Skeleton className="h-9 w-full" />
        </div>
      </div>
    </div>
  );
}

function RiskIndicatorSkeleton() {
  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
      <div className="bg-gradient-to-r from-slate-700 to-slate-600 p-6">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Skeleton className="w-14 h-14 rounded-2xl" />
            <div className="space-y-2">
              <Skeleton className="h-4 w-24" />
              <Skeleton className="h-7 w-40" />
            </div>
          </div>
          <Skeleton className="h-8 w-28 rounded-full" />
        </div>
      </div>
      <div className="p-6 space-y-6">
        <div className="space-y-2">
          <div className="flex justify-between">
            <Skeleton className="h-4 w-20" />
            <Skeleton className="h-6 w-16" />
          </div>
          <Skeleton className="h-4 w-full rounded-full" />
        </div>
        <div className="grid grid-cols-4 gap-3">
          {[...Array(4)].map((_, i) => (
            <div key={i} className="bg-slate-700/50 rounded-xl p-4 text-center">
              <Skeleton className="h-8 w-12 mx-auto mb-2" />
              <Skeleton className="h-3 w-16 mx-auto" />
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function StatsSkeleton() {
  return (
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
      {[...Array(4)].map((_, i) => (
        <div key={i} className="bg-slate-800/40 rounded-xl p-4 text-center border border-slate-700/50">
          <Skeleton className="h-5 w-5 mx-auto mb-2" />
          <Skeleton className="h-8 w-16 mx-auto mb-1" />
          <Skeleton className="h-3 w-20 mx-auto" />
        </div>
      ))}
    </div>
  );
}

function TableRowSkeleton() {
  return (
    <div className="flex items-center gap-4 p-4 border-b border-slate-700/50">
      <Skeleton className="h-10 w-10 rounded-full bg-slate-700" />
      <div className="flex-1 space-y-2">
        <Skeleton className="h-4 w-1/3 bg-slate-700" />
        <Skeleton className="h-3 w-1/4 bg-slate-700" />
      </div>
      <Skeleton className="h-6 w-20 bg-slate-700" />
      <Skeleton className="h-8 w-24 bg-slate-700" />
    </div>
  );
}

function TableSkeleton({ rows = 5 }: { rows?: number }) {
  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 overflow-hidden">
      <div className="flex items-center gap-4 p-4 border-b border-slate-700 bg-slate-800/80">
        <Skeleton className="h-4 w-24 bg-slate-600" />
        <Skeleton className="h-4 w-32 bg-slate-600" />
        <Skeleton className="h-4 w-20 bg-slate-600" />
        <Skeleton className="h-4 w-28 bg-slate-600" />
      </div>
      {[...Array(rows)].map((_, i) => (
        <TableRowSkeleton key={i} />
      ))}
    </div>
  );
}

function FormSkeleton() {
  return (
    <div className="space-y-6">
      <div className="space-y-2">
        <Skeleton className="h-4 w-24 bg-slate-700" />
        <Skeleton className="h-10 w-full bg-slate-700" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-32 bg-slate-700" />
        <Skeleton className="h-10 w-full bg-slate-700" />
      </div>
      <div className="space-y-2">
        <Skeleton className="h-4 w-28 bg-slate-700" />
        <Skeleton className="h-24 w-full bg-slate-700" />
      </div>
      <Skeleton className="h-10 w-32 bg-slate-700" />
    </div>
  );
}

function DashboardCardSkeleton() {
  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6 space-y-4">
      <div className="flex items-center justify-between">
        <Skeleton className="h-5 w-32 bg-slate-700" />
        <Skeleton className="h-8 w-8 rounded-lg bg-slate-700" />
      </div>
      <Skeleton className="h-10 w-24 bg-slate-700" />
      <Skeleton className="h-3 w-full bg-slate-700" />
    </div>
  );
}

function ChartSkeleton() {
  return (
    <div className="bg-slate-800/50 rounded-xl border border-slate-700 p-6">
      <div className="flex items-center justify-between mb-6">
        <Skeleton className="h-6 w-40 bg-slate-700" />
        <Skeleton className="h-8 w-24 bg-slate-700" />
      </div>
      <div className="h-64 flex items-end justify-between gap-2">
        {[...Array(12)].map((_, i) => (
          <Skeleton 
            key={i} 
            className="flex-1 bg-slate-700" 
            style={{ height: `${Math.random() * 80 + 20}%` }} 
          />
        ))}
      </div>
    </div>
  );
}

function ProfileSkeleton() {
  return (
    <div className="space-y-6">
      <div className="flex items-center gap-4">
        <Skeleton className="h-20 w-20 rounded-full bg-slate-700" />
        <div className="space-y-2">
          <Skeleton className="h-6 w-40 bg-slate-700" />
          <Skeleton className="h-4 w-56 bg-slate-700" />
        </div>
      </div>
      <div className="grid grid-cols-2 gap-4">
        <DashboardCardSkeleton />
        <DashboardCardSkeleton />
      </div>
    </div>
  );
}

export { 
  Skeleton, 
  CardSkeleton, 
  TimelineCardSkeleton, 
  RiskIndicatorSkeleton, 
  StatsSkeleton,
  TableRowSkeleton,
  TableSkeleton,
  FormSkeleton,
  DashboardCardSkeleton,
  ChartSkeleton,
  ProfileSkeleton
};
